import AttorneyHubAPIService from "_shared/_api/attorney-hub-api-service";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import PaginatedResponse from "_shared/_api/pagination/responses/paginated-response";
import { assertIsAxiosError } from "_shared/_errors/errors";
import Guid from "_shared/guids/guid";
import axios, { AxiosHeaders, AxiosResponse, CanceledError, HttpStatusCode } from "axios";
import Individual from "marketplace/individuals/individual";
import BulkMessageAPIRequest from "messaging/_api/commands/bulk-message-api-command";
import CreateMessageAPIRequest from "messaging/_api/commands/create-message-api-command";
import MessageParameters from "messaging/_api/queries/message-parameters";
import AttachmentParameters from "messaging/attachments/_api/queries/attachment-parameters";
import MessageFileInfo from "messaging/attachments/file-info/message-file-info";
import ForumQuery from "messaging/forums/_api/queries/forum-query";
import Forum, { ForumDoesNotExistError, UnauthorizedForumAccessException } from "messaging/forums/forum";
import MarketplaceSubscriberInfoAPIResponse from "messaging/forums/subscribers/_api/responses/marketplace-subscriber-info-api-response";
import ForumSubscriberInvitationCommand from "messaging/forums/subscribers/invitations/_apis/commands/forum-subscriber-invitation-api-command";
import ForumInvitation, {
  ForumInvitationDoesNotExistError,
} from "messaging/forums/subscribers/invitations/forum-invitation";
import Message from "messaging/message";
import Session from "users/__session/session";
import ForumSchema, { ForumJSON } from "messaging/forums/_schemas/forum-schema";
import MessageSchema from "messaging/_schemas/message-schema";

export default class MessagingAPIService {
  public constructor(session: Readonly<Session>) {
    if (!session.authToken?.value) {
      throw new Error("Session must have an authToken to create a MessagingAPIService");
    }

    this.authHeaders.set("Authorization", `Bearer ${session.authToken.value}`);
  }

  public async getForums(queryRequest: ForumQuery, abortController?: AbortController): Promise<ForumJSON[]> {
    let url = new URL("/messaging/forums", AttorneyHubAPIService.apiBaseUrl);
    try {
      url.search = queryRequest.searchParams.toString();
      const response = await axios.get(url.toString(), {
        headers: this.authHeaders,
        signal: abortController?.signal,
      });
      return ForumSchema.validateJSONArray(response.data);
    } catch (error) {
      assertIsAxiosError(error);
      if (error.response?.status === HttpStatusCode.BadRequest)
        throw new ForumDoesNotExistError(String(error.response?.data));
      if (error instanceof CanceledError) throw error;
      throw new MessagingAPIServiceError("getForums", error);
    }
  }

  public async getForumsByIds(ids: Guid[], abortController?: AbortController): Promise<ForumJSON[]> {
    let url = new URL("/messaging/forums/by-ids", AttorneyHubAPIService.apiBaseUrl);
    try {
      const response = await axios.post(
        url.toString(),
        ids.map((id) => id.value),
        {
          headers: this.authHeaders,
          signal: abortController?.signal,
        },
      );
      return ForumSchema.validateJSONArray(response.data);
    } catch (error) {
      assertIsAxiosError(error);
      if (error.response?.status === HttpStatusCode.BadRequest)
        throw new ForumDoesNotExistError(String(error.response?.data));
      if (error instanceof CanceledError) throw error;
      throw new MessagingAPIServiceError("getForumsByIds", error);
    }
  }

  public async getMessagesByForum(
    forum: Forum,
    query: MessageQuery,
    abortController?: AbortController,
  ): Promise<PaginatedResponse<Message>> {
    if (!forum.id)
      throw new MessagingAPIServiceError("getMessagesByForum", new Error("Forum must have an id to get messages"));

    try {
      const url = new URL(`/messaging/forums/${forum.id.value}/messages`, AttorneyHubAPIService.apiBaseUrl);
      for (const [key, value] of query.searchParams) {
        url.searchParams.append(key, value);
      }
      const response = await axios.get(url.toString(), {
        headers: this.authHeaders,
        signal: abortController?.signal,
      });
      const responseData = Message.fromJSONArray({ jsonArray: response.data });
      return PaginatedResponse.from<Message>(responseData, response.headers["x-pagination"]);
    } catch (error) {
      assertIsAxiosError(error);
      if (error instanceof CanceledError) throw error;
      if (error.response?.status === HttpStatusCode.BadRequest)
        throw new ForumDoesNotExistError(String(error.response.data));
      if (error.response?.status === HttpStatusCode.Forbidden) throw new UnauthorizedForumAccessException(forum.id);
      if (error instanceof CanceledError) throw error;
      throw new MessagingAPIServiceError("getMessagesByForum", error);
    }
  }

  public async getForumSubscriberInfo(forum: Forum, abortController?: AbortController): Promise<Individual[]> {
    if (!forum.id)
      throw new MessagingAPIServiceError(
        "getForumSubscriberInfo",
        new Error("Forum must have an id to get subscribers"),
      );
    const url = new URL(`/messaging/forums/${forum.id.value}/subscribers`, AttorneyHubAPIService.apiBaseUrl);
    try {
      const response = await axios.get(url.toString(), {
        headers: this.authHeaders,
        signal: abortController?.signal,
      });
      const responseData: MarketplaceSubscriberInfoAPIResponse[] = response.data.map(
        (info: Partial<MarketplaceSubscriberInfoAPIResponse>) => {
          return Object.assign(new MarketplaceSubscriberInfoAPIResponse({}), info);
        },
      );
      return responseData.map((info) => info.deserialize());
    } catch (error) {
      assertIsAxiosError(error);
      if (error.response?.status === HttpStatusCode.NotFound)
        throw new ForumDoesNotExistError(String(error.response.data));
      if (error instanceof CanceledError) {
        throw error;
      }
      throw new MessagingAPIServiceError("getForumSubscriberInfo", error);
    }
  }

  public async getMessageImages(message: Message, abortController?: AbortController): Promise<MessageFileInfo[]> {
    if (!message.id || !message.forum?.id) {
      throw new MessagingAPIServiceError(
        "getMessageImages",
        new Error("Message must have an id and forum id to get images"),
      );
    }
    const url = new URL(
      `/messaging/forums/${message.forum.id.value}/messages/${message.id.value}/images`,
      AttorneyHubAPIService.apiBaseUrl,
    );
    try {
      const response = await axios.get(url.toString(), {
        headers: this.authHeaders,
        signal: abortController?.signal,
      });
      return MessageFileInfo.fromJSONArray(response.data);
    } catch (error) {
      assertIsAxiosError(error);
      if (error.response?.status === HttpStatusCode.NotFound)
        throw new ForumDoesNotExistError(String(error.response.data));
      if (error.response?.status === HttpStatusCode.Forbidden)
        throw new UnauthorizedForumAccessException(message.forum.id);
      if (error instanceof CanceledError) {
        throw error;
      }
      throw new MessagingAPIServiceError("getMessageImages", error);
    }
  }

  public async getMessageAttachmentById(
    forumId: Guid | undefined,
    fileId: Guid,
    abortController?: AbortController,
  ): Promise<AxiosResponse> {
    if (!forumId)
      throw new MessagingAPIServiceError(
        "getMessageAttachmentById",
        new Error("Must have a forum id to get attachments"),
      );
    const url = new URL(`/messaging/forums/${forumId}/attachments/${fileId.value}`, AttorneyHubAPIService.apiBaseUrl);
    try {
      return await axios.get(url.toString(), {
        headers: this.authHeaders,
        responseType: "blob",
        signal: abortController?.signal,
      });
    } catch (error) {
      assertIsAxiosError(error);
      if (error.response?.status === HttpStatusCode.Forbidden) throw new UnauthorizedForumAccessException(forumId);
      if (error.response?.status === HttpStatusCode.NotFound)
        throw new ForumDoesNotExistError(String(error.response.data));
      throw new MessagingAPIServiceError("getMessageAttachmentByIndex", error);
    }
  }

  public async getAttachmentsByForum(
    forum: Forum,
    query: AttachmentQuery,
    abortController?: AbortController,
  ): Promise<PaginatedResponse<MessageFileInfo>> {
    if (!forum.id)
      throw new MessagingAPIServiceError(
        "GetAttachmentsByForum",
        new Error("Forum must have an id to get attachments"),
      );

    try {
      const url = new URL(`/messaging/forums/${forum.id.value}/attachments`, AttorneyHubAPIService.apiBaseUrl);
      for (const [key, value] of query.searchParams) {
        url.searchParams.append(key, value);
      }
      const response = await axios.get(url.toString(), {
        headers: this.authHeaders,
        signal: abortController?.signal,
      });
      const responseData = MessageFileInfo.fromJSONArray(response.data);
      return PaginatedResponse.from<MessageFileInfo>(responseData, response.headers["x-pagination"]);
    } catch (error) {
      assertIsAxiosError(error);
      if (error instanceof CanceledError) throw error;
      if (error.response?.status === HttpStatusCode.Forbidden) throw new UnauthorizedForumAccessException(forum.id);
      if (error.response?.status === HttpStatusCode.NotFound)
        throw new ForumDoesNotExistError(String(error.response.data));
      if (error instanceof CanceledError) {
        throw error;
      }
      throw new MessagingAPIServiceError("GetAttachmentsByForum", error);
    }
  }

  public async createMessage(message: Message): Promise<Message> {
    if (!message.forum?.id)
      throw new MessagingAPIServiceError("createMessage", new Error("Message must have a forum id to be created"));
    const request = new CreateMessageAPIRequest(message);
    const url = new URL(`/messaging/forums/${message.forum.id.value}/messages`, AttorneyHubAPIService.apiBaseUrl);
    try {
      const response = await axios.post(url.toString(), request.formData, {
        headers: this.headersWithFormData(),
      });
      const params = MessageSchema.deserialize(response.data);
      return new Message(params);
    } catch (error) {
      assertIsAxiosError(error);
      if (error.response?.status === HttpStatusCode.NotFound)
        throw new ForumDoesNotExistError(String(error.response.data));
      throw new MessagingAPIServiceError("createMessage", error);
    }
  }

  public async createBulkMessages(messages: Message[]): Promise<Message[]> {
    if (!messages.length) {
      console.warn("No messages to create");
      return messages;
    }
    const request = new BulkMessageAPIRequest(messages);
    const url = new URL(`/messaging/bulk-messages`, AttorneyHubAPIService.apiBaseUrl);
    try {
      const response = await axios.post(url.toString(), request.payload, {
        headers: this.headersWithJson(),
      });
      const responseData = Message.fromJSONArray(response.data);
      return responseData;
    } catch (error) {
      assertIsAxiosError(error);
      if (error.response?.status === HttpStatusCode.NotFound)
        throw new ForumDoesNotExistError(String(error.response.data));
      throw new MessagingAPIServiceError("createBulkMessages", error);
    }
  }

  public async createForum(forum: Forum): Promise<Forum> {
    const url = new URL("/messaging/forums", AttorneyHubAPIService.apiBaseUrl);
    try {
      const response = await axios.post(url.toString(), forum.toJSON(), {
        headers: this.headersWithJson(),
      });
      const params = ForumSchema.deserialize(response.data);
      return new Forum(params);
    } catch (error) {
      assertIsAxiosError(error);
      if (error.response?.status === HttpStatusCode.NotFound)
        throw new ForumDoesNotExistError(String(error.response.data));
      throw new MessagingAPIServiceError("createForum", error);
    }
  }

  public async updateForum(_forum: Forum): Promise<Forum> {
    throw new MessagingAPIServiceError("updateForum", new Error("Method not implemented."));
  }

  public async resolveForum(forum: Forum) {
    if (!forum.id)
      throw new MessagingAPIServiceError("resolveForum", new Error("Forum must have an id to be resolved"));
    const url = new URL(`/messaging/forums/${forum.id.value}/resolve`, AttorneyHubAPIService.apiBaseUrl);
    try {
      await axios.post(
        url.toString(),
        {},
        {
          headers: this.authHeaders,
        },
      );
    } catch (error) {
      assertIsAxiosError(error);
      if (error.response?.status === HttpStatusCode.NotFound)
        throw new ForumDoesNotExistError(String(error.response.data));
      throw new MessagingAPIServiceError("resolveForum", error);
    }
  }

  public async reopenForum(forum: Forum) {
    if (!forum.id) throw new MessagingAPIServiceError("reopenForum", new Error("Forum must have an id to be reopened"));
    const url = new URL(`/messaging/forums/${forum.id.value}/reopen`, AttorneyHubAPIService.apiBaseUrl);
    try {
      await axios.post(
        url.toString(),
        {},
        {
          headers: this.authHeaders,
        },
      );
    } catch (error) {
      assertIsAxiosError(error);
      if (error.response?.status === HttpStatusCode.NotFound)
        throw new ForumDoesNotExistError(String(error.response.data));
      throw new MessagingAPIServiceError("reopenForum", error);
    }
  }

  public async updateSubscribersForForum(forum: Forum) {
    if (!forum.id)
      throw new MessagingAPIServiceError(
        "updateSubscribersForForum",
        new Error("Forum must have an id to update subscribers"),
      );
    const url = new URL(`/messaging/forums/${forum.id.value}/subscribers`, AttorneyHubAPIService.apiBaseUrl);
    try {
      await axios.put(
        url.toString(),
        {
          subscribers:
            forum.subscribers?.map((subscriber) => {
              return {
                userId: subscriber.userId,
                legalEntityId: subscriber.legalEntityId,
              };
            }) ?? [],
        },
        {
          headers: this.headersWithJson(),
        },
      );
    } catch (error) {
      assertIsAxiosError(error);
      if (error.response?.status === HttpStatusCode.NotFound)
        throw new ForumDoesNotExistError(String(error.response.data));
      throw new MessagingAPIServiceError("updateSubscribersForForum", error);
    }
  }

  public async inviteSubscriberToForum(forum: Forum, invitee: Guid, invitationMessage?: string) {
    if (!forum.id)
      throw new MessagingAPIServiceError(
        "inviteSubscriberToForum",
        new Error("Forum must have an id to invite a subscriber"),
      );
    const url = new URL(`/messaging/forums/${forum.id.value}/invite-subscriber`, AttorneyHubAPIService.apiBaseUrl);
    try {
      await axios.post(url.toString(), new ForumSubscriberInvitationCommand(invitee, invitationMessage), {
        headers: this.headersWithJson(),
      });
    } catch (error) {
      assertIsAxiosError(error);
      if (error.response?.status === HttpStatusCode.NotFound)
        throw new ForumDoesNotExistError(String(error.response.data));
      throw new MessagingAPIServiceError("inviteSubscriberToForum", error);
    }
  }

  public async acceptForumInvitation(forumInvitation: ForumInvitation) {
    const url = new URL(
      `/messaging/forum-invitations/${forumInvitation.id.value}/accept`,
      AttorneyHubAPIService.apiBaseUrl,
    );
    try {
      await axios.post(
        url.toString(),
        {},
        {
          headers: this.authHeaders,
        },
      );
    } catch (error) {
      assertIsAxiosError(error);
      if (error.response?.status === HttpStatusCode.NotFound)
        throw new ForumInvitationDoesNotExistError(String(error.response.data));
      throw new MessagingAPIServiceError("acceptForumInvitation", error);
    }
  }

  public async deleteMessage(message: Message) {
    if (!message.id) {
      throw new MessagingAPIServiceError("deleteMessage", new Error("Message must have an id to be deleted"));
    }
    if (!message.forum?.id) {
      throw new MessagingAPIServiceError("deleteMessage", new Error("Message must have a forum id to be deleted"));
    }
    const url = new URL(
      `/messaging/forums/${message.forum.id.value}/messages/${message.id.value}`,
      AttorneyHubAPIService.apiBaseUrl,
    );
    try {
      await axios.delete(url.toString(), {
        headers: this.authHeaders,
      });
    } catch (error) {
      assertIsAxiosError(error);
      if (error.response?.status === HttpStatusCode.NotFound)
        throw new MessageNotFoundError(String(error.response.data));
      throw new MessagingAPIServiceError("deleteMessage", error);
    }
  }

  public async deleteForum(forum: Forum) {
    if (!forum.id) {
      throw new MessagingAPIServiceError("deleteForum", new Error("Forum must have an id to be deleted"));
    }

    const url = new URL(`/messaging/forums/${forum.id.value}`, AttorneyHubAPIService.apiBaseUrl);
    try {
      await axios.delete(url.toString(), {
        headers: this.authHeaders,
      });
    } catch (error) {
      assertIsAxiosError(error);
      if (error.response?.status === HttpStatusCode.NotFound)
        throw new ForumDoesNotExistError(String(error.response.data));
      throw new MessagingAPIServiceError("deleteForum", error);
    }
  }

  private readonly authHeaders: AxiosHeaders = new AxiosHeaders();

  private headersWithJson(): AxiosHeaders {
    return this.authHeaders.concat({ "Content-Type": "application/json-patch+json" });
  }

  private headersWithFormData(): AxiosHeaders {
    return this.authHeaders.concat({ "Content-Type": "multipart/form-data" });
  }
}

export class MessageQuery {
  public readonly messageParameters: MessageParameters;
  public readonly paginationParameters: PaginationParameters;

  constructor(messageParameters: MessageParameters, paginationParameters: PaginationParameters) {
    this.paginationParameters = paginationParameters;
    this.messageParameters = messageParameters;
  }

  public get searchParams(): URLSearchParams {
    return new URLSearchParams([...this.paginationParameters.searchParams, ...this.messageParameters.searchParams]);
  }

  public isEqualTo(other: MessageQuery): boolean {
    if (!other) return false;
    return (
      this.messageParameters.isEqualTo(other.messageParameters) &&
      this.paginationParameters.isEqualTo(other.paginationParameters)
    );
  }
}

export class AttachmentQuery {
  public readonly attachmentParameters: AttachmentParameters;
  public readonly paginationParameters: PaginationParameters;

  constructor(attachmentParameters: AttachmentParameters, paginationParameters: PaginationParameters) {
    this.paginationParameters = paginationParameters;
    this.attachmentParameters = attachmentParameters;
  }

  public get searchParams(): URLSearchParams {
    return new URLSearchParams([...this.paginationParameters.searchParams, ...this.attachmentParameters.searchParams]);
  }

  public isEqualTo(other: AttachmentQuery): boolean {
    if (!other) return false;
    return (
      this.attachmentParameters.isEqualTo(other.attachmentParameters) &&
      this.paginationParameters.isEqualTo(other.paginationParameters)
    );
  }
}

export class MessagingAPIServiceError extends Error {
  method: string;
  error: Error;

  constructor(method: string, error: Error) {
    super(`Error calling ${method}: ${error}`);
    this.method = method;
    this.error = error;
  }
}

export class MessageNotFoundError extends Error {
  constructor(messageId: string) {
    super(`Message ${messageId} not found`);
  }
}

export class AttachmentNotFoundError extends Error {
  constructor(url: string) {
    super(`Attachment(s) not found at ${url}`);
  }
}
